import { logger } from "@/lib/logger";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

interface GenerateInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function GenerateInvoiceDialog({ open, onOpenChange, onSuccess }: GenerateInvoiceDialogProps) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    trial_site_id: "",
    sponsor_organization_id: "",
    trial_id: "",
    billing_period_start: "",
    billing_period_end: "",
    notes: "",
    auto_send: false
  });

  // Fetch trial sites - use 'name' column (the actual column in schema)
  const { data: sites } = useQuery({
    queryKey: ['trial-sites-for-billing'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trial_sites_safe')
        .select('id, name')
        .eq('status', 'active')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: open
  });

  // Fetch organizations (sponsors)
  const { data: sponsors } = useQuery({
    queryKey: ['sponsor-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: open
  });

  // Fetch active trials for selected site and sponsor
  const { data: trials } = useQuery({
    queryKey: ['active-trials-for-billing', formData.trial_site_id, formData.sponsor_organization_id],
    queryFn: async () => {
      if (!formData.trial_site_id || !formData.sponsor_organization_id) return [];
      
      const { data, error } = await (supabase as any)
        .from('cro_trial_sites')
        .select(`
          trial_id,
          cro_trials!inner(
            id,
            trial_title,
            sponsor_organization_id
          )
        `)
        .eq('trial_site_id', formData.trial_site_id)
        .eq('cro_trials.sponsor_organization_id', formData.sponsor_organization_id)
        .eq('status', 'active');
      
      if (error) throw error;
      return data?.map((item: any) => ({
        trial_id: item.cro_trials.id,
        title: item.cro_trials.trial_title
      })) || [];
    },
    enabled: open && !!formData.trial_site_id && !!formData.sponsor_organization_id
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.trial_site_id || !formData.sponsor_organization_id || !formData.trial_id || 
        !formData.billing_period_start || !formData.billing_period_end) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // Validate date range
    if (new Date(formData.billing_period_end) <= new Date(formData.billing_period_start)) {
      toast({
        title: "Validation Error",
        description: "End date must be after start date",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-sponsor-invoice', {
        body: formData
      });

      if (error) throw error;

      toast({
        title: "Invoice Generated",
        description: `Invoice ${data.invoice.invoice_number} created successfully with ${data.invoice.line_items_count} line items`,
      });

      onOpenChange(false);
      onSuccess?.();
      
      // Reset form
      setFormData({
        trial_site_id: "",
        sponsor_organization_id: "",
        trial_id: "",
        billing_period_start: "",
        billing_period_end: "",
        notes: "",
        auto_send: false
      });
    } catch (error: any) {
      logger.error('Error generating invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate invoice",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Generate Sponsor Invoice</DialogTitle>
          <DialogDescription>
            Create a new invoice from pending billable events for the selected billing period
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Trial Site Selection */}
          <div className="space-y-2">
            <Label htmlFor="trial_site_id">Trial Site *</Label>
            <Select 
              value={formData.trial_site_id} 
              onValueChange={(value) => setFormData({ ...formData, trial_site_id: value, trial_id: "" })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select trial site" />
              </SelectTrigger>
              <SelectContent>
                {sites?.map((site) => (
                  <SelectItem key={site.id} value={site.id}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sponsor Selection */}
          <div className="space-y-2">
            <Label htmlFor="sponsor_organization_id">Sponsor Organization *</Label>
            <Select 
              value={formData.sponsor_organization_id} 
              onValueChange={(value) => setFormData({ ...formData, sponsor_organization_id: value, trial_id: "" })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sponsor" />
              </SelectTrigger>
              <SelectContent>
                {sponsors?.map((sponsor) => (
                  <SelectItem key={sponsor.id} value={sponsor.id}>
                    {sponsor.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Trial Selection */}
          <div className="space-y-2">
            <Label htmlFor="trial_id">Clinical Trial *</Label>
            <Select 
              value={formData.trial_id} 
              onValueChange={(value) => setFormData({ ...formData, trial_id: value })}
              disabled={!formData.trial_site_id || !formData.sponsor_organization_id}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select trial" />
              </SelectTrigger>
              <SelectContent>
                {trials?.map((trial: any) => (
                  <SelectItem key={trial.trial_id} value={trial.trial_id}>
                    {trial.title?.substring(0, 80) || trial.trial_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Billing Period */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="billing_period_start">Period Start *</Label>
              <Input
                id="billing_period_start"
                type="date"
                value={formData.billing_period_start}
                onChange={(e) => setFormData({ ...formData, billing_period_start: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billing_period_end">Period End *</Label>
              <Input
                id="billing_period_end"
                type="date"
                value={formData.billing_period_end}
                onChange={(e) => setFormData({ ...formData, billing_period_end: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any additional notes for this invoice..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          {/* Auto Send */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="auto_send"
              checked={formData.auto_send}
              onChange={(e) => setFormData({ ...formData, auto_send: e.target.checked })}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="auto_send" className="text-sm font-normal cursor-pointer">
              Send invoice email immediately after generation
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={generating}>
              {generating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Invoice'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
